/* eslint-disable react/no-unknown-property */
import { motion } from "framer-motion";

/**
 * Engineering decorative graphics library.
 *
 * The default export remains the maroon bouncing gears used in WhyChooseUs.
 * Named exports below are subtle navy/blue blueprint ornaments to use elsewhere.
 */
const MAROON = "#7A1F2E";
const MAROON_SOFT = "#9B2D3F";

const TONES = {
  navy: "#7A1F2E",
  blue: "#9B2D3F",
  orange: "#F5A623",
  white: "#FFFFFF",
  maroon: "#7A1F2E",
};

/* -------------------- MAROON BOUNCING GEAR CLUSTER (default) -------------------- */
function Gear({ size, teeth, spin, reverse = false, color = MAROON }) {
  const r = size / 2;
  const innerR = r * 0.62;
  const toothH = (r * 0.96 - innerR) * 0.6;
  const items = [];
  for (let i = 0; i < teeth; i++) {
    const a = (i / teeth) * 360;
    items.push(
      <rect
        key={i}
        x={-2.5}
        y={-r}
        width={5}
        height={toothH}
        transform={`rotate(${a})`}
        fill={color}
      />
    );
  }
  return (
    <motion.g
      animate={{ rotate: reverse ? -360 : 360 }}
      transition={{ repeat: Infinity, ease: "linear", duration: spin }}
      style={{ transformOrigin: "center" }}
    >
      <circle r={r * 0.9} fill="none" stroke={color} strokeWidth={1.6} />
      <circle r={r * 0.62} fill="none" stroke={color} strokeWidth={1.4} />
      <circle r={r * 0.18} fill="none" stroke={color} strokeWidth={1.4} />
      {items}
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <line
          key={deg}
          x1={0}
          y1={-r * 0.62}
          x2={0}
          y2={-r * 0.2}
          stroke={color}
          strokeWidth={1.4}
          transform={`rotate(${deg})`}
        />
      ))}
    </motion.g>
  );
}

export default function MaroonBouncingGraphic() {
  return (
    <motion.div
      aria-hidden
      data-testid="maroon-bouncing-graphic"
      className="pointer-events-none absolute inset-y-0 right-0 hidden md:flex items-center justify-end w-[36rem] max-w-[55%]"
      animate={{ x: [0, -160, 0, -160, 0] }}
      transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
    >
      <svg
        viewBox="-220 -220 440 440"
        className="w-[28rem] h-[28rem] drop-shadow-[0_8px_30px_rgba(122,31,46,0.18)]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g transform="translate(-30,-10)">
          <Gear size={260} teeth={20} spin={26} />
        </g>
        <g transform="translate(120,90)">
          <Gear size={150} teeth={16} spin={18} reverse color={MAROON_SOFT} />
        </g>
        <g transform="translate(135,-105)">
          <Gear size={88} teeth={12} spin={12} color={MAROON_SOFT} />
        </g>
        <g fill="none" stroke={MAROON} strokeWidth="1" opacity="0.55">
          <line x1="-200" y1="180" x2="-100" y2="180" />
          <line x1="-200" y1="175" x2="-200" y2="185" />
          <line x1="-100" y1="175" x2="-100" y2="185" />
        </g>
        <text
          x="-180"
          y="200"
          fontSize="11"
          fill={MAROON}
          opacity="0.6"
          fontFamily="monospace"
        >
          Ø 220.00
        </text>
      </svg>
    </motion.div>
  );
}

/* -------------------- NAVY BLUEPRINT ORNAMENTS -------------------- */

function Wrap({ className = "", opacity = 0.08, children, animate = false, bounce = false }) {
  let animProps = {};
  if (animate) {
    animProps = {
      animate: { y: [0, -10, 0] },
      transition: { repeat: Infinity, duration: 5, ease: "easeInOut" },
    };
  }
  if (bounce) {
    animProps = {
      animate: { x: [0, -24, 0, 24, 0] },
      transition: { repeat: Infinity, duration: 7, ease: "easeInOut" },
    };
  }
  return (
    <motion.div
      aria-hidden
      className={`pointer-events-none absolute select-none ${className}`}
      style={{ opacity }}
      {...animProps}
    >
      {children}
    </motion.div>
  );
}

/** Blueprint of a CNC milling machine (front view + dimensions). */
export function BlueprintMachine({ className = "", opacity = 0.32, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 600 480" className="w-[34rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {/* base */}
          <rect x="80" y="380" width="440" height="40" />
          <line x1="100" y1="380" x2="100" y2="360" />
          <line x1="500" y1="380" x2="500" y2="360" />
          <rect x="120" y="340" width="360" height="40" />
          {/* column */}
          <rect x="360" y="120" width="120" height="220" />
          <line x1="360" y1="160" x2="480" y2="160" />
          <line x1="360" y1="200" x2="480" y2="200" />
          <line x1="360" y1="240" x2="480" y2="240" />
          {/* spindle head */}
          <rect x="200" y="160" width="180" height="50" />
          <line x1="240" y1="210" x2="240" y2="280" strokeWidth="3" />
          <rect x="232" y="280" width="16" height="14" />
          {/* tool */}
          <polygon points="234,294 246,294 240,306" />
          {/* table */}
          <rect x="120" y="320" width="320" height="20" />
          <line x1="140" y1="320" x2="140" y2="340" />
          <line x1="180" y1="320" x2="180" y2="340" />
          <line x1="220" y1="320" x2="220" y2="340" />
          <line x1="260" y1="320" x2="260" y2="340" />
          <line x1="300" y1="320" x2="300" y2="340" />
          <line x1="340" y1="320" x2="340" y2="340" />
          <line x1="380" y1="320" x2="380" y2="340" />
          <line x1="420" y1="320" x2="420" y2="340" />
          {/* workpiece */}
          <rect x="220" y="300" width="80" height="20" />
          {/* control panel */}
          <rect x="500" y="200" width="60" height="100" />
          <circle cx="530" cy="220" r="4" />
          <circle cx="530" cy="240" r="4" />
          <rect x="510" y="260" width="40" height="30" />
          {/* dimensions */}
          <line x1="80" y1="450" x2="520" y2="450" strokeDasharray="0" />
          <line x1="80" y1="445" x2="80" y2="455" />
          <line x1="520" y1="445" x2="520" y2="455" />
          <text x="280" y="468" fontSize="11" fill={c} fontFamily="monospace">
            1840 mm
          </text>
          <line x1="40" y1="120" x2="40" y2="420" />
          <line x1="35" y1="120" x2="45" y2="120" />
          <line x1="35" y1="420" x2="45" y2="420" />
          <text x="10" y="280" fontSize="11" fill={c} fontFamily="monospace" transform="rotate(-90 10 280)">
            2100 mm
          </text>
          {/* axis labels */}
          <line x1="540" y1="380" x2="580" y2="380" />
          <line x1="540" y1="380" x2="540" y2="340" />
          <text x="565" y="375" fontSize="10" fill={c} fontFamily="monospace">X</text>
          <text x="544" y="338" fontSize="10" fill={c} fontFamily="monospace">Z</text>
        </g>
      </svg>
    </Wrap>
  );
}

/** Robotic arm silhouette (jointed industrial robot). */
export function RoboticArm({ className = "", opacity = 0.35, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 400 500" className="w-[20rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          {/* base */}
          <rect x="110" y="430" width="180" height="50" />
          <rect x="140" y="410" width="120" height="20" />
          <circle cx="200" cy="400" r="16" />
          <circle cx="200" cy="400" r="5" fill={c} />
          {/* lower arm */}
          <rect x="178" y="240" width="44" height="170" transform="rotate(-8 200 325)" />
          <circle cx="200" cy="400" r="6" fill={c} />
          {/* elbow */}
          <circle cx="172" cy="260" r="20" />
          <circle cx="172" cy="260" r="6" fill={c} />
          {/* upper arm */}
          <rect x="170" y="140" width="44" height="130" transform="rotate(30 192 205)" />
          {/* wrist */}
          <circle cx="280" cy="130" r="16" />
          <circle cx="280" cy="130" r="5" fill={c} />
          {/* gripper */}
          <line x1="280" y1="130" x2="320" y2="80" strokeWidth="3" />
          <line x1="316" y1="84" x2="306" y2="60" />
          <line x1="320" y1="80" x2="332" y2="62" />
          <line x1="306" y1="60" x2="298" y2="48" />
          <line x1="332" y1="62" x2="340" y2="48" />
          {/* tooling target */}
          <circle cx="320" cy="42" r="14" strokeDasharray="3 4" />
          <line x1="306" y1="42" x2="334" y2="42" />
          <line x1="320" y1="28" x2="320" y2="56" />
          {/* control cable */}
          <path d="M 290 480 C 320 470 340 460 360 440" />
          <path d="M 290 478 C 322 466 344 456 364 436" />
          {/* base label */}
          <rect x="150" y="475" width="100" height="14" />
          <text x="158" y="486" fontSize="9" fill={c} fontFamily="monospace">TGS-R6 · 6-AXIS</text>
        </g>
      </svg>
    </Wrap>
  );
}

/** Welding torch with spark burst (animated sparks). */
export function WeldingTorch({ className = "", opacity = 0.4, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  const sparks = Array.from({ length: 14 });
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 400 400" className="w-[18rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          {/* hand grip */}
          <rect x="40" y="280" width="120" height="38" rx="6" />
          <line x1="60" y1="280" x2="60" y2="318" />
          <line x1="80" y1="280" x2="80" y2="318" />
          <line x1="100" y1="280" x2="100" y2="318" />
          <line x1="120" y1="280" x2="120" y2="318" />
          <line x1="140" y1="280" x2="140" y2="318" />
          {/* trigger */}
          <path d="M 90 318 L 100 340 L 130 340 L 140 318" />
          {/* neck/nozzle */}
          <line x1="160" y1="290" x2="240" y2="220" strokeWidth="6" />
          <polygon points="232,212 260,200 248,228" />
          {/* arc cone */}
          <path d="M 256 208 L 280 188 M 256 218 L 286 210 M 250 222 L 282 222" />
          {/* workpiece */}
          <rect x="220" y="280" width="160" height="28" />
          <line x1="220" y1="295" x2="380" y2="295" strokeDasharray="3 4" />
          {/* cable */}
          <path d="M 40 300 C 10 320 10 360 40 380" />
        </g>
        {/* animated sparks */}
        <g>
          {sparks.map((_, i) => {
            const angle = (i / sparks.length) * Math.PI - Math.PI / 2;
            const dx = Math.cos(angle) * 60;
            const dy = Math.sin(angle) * 60;
            return (
              <motion.circle
                key={i}
                cx={270}
                cy={205}
                r={1.6}
                fill={TONES.orange}
                animate={{
                  cx: [270, 270 + dx],
                  cy: [205, 205 + dy + 30],
                  opacity: [1, 0],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 1.4 + (i % 3) * 0.3,
                  delay: (i * 0.12) % 1.2,
                  ease: "easeOut",
                }}
              />
            );
          })}
        </g>
      </svg>
    </Wrap>
  );
}

/** Digital caliper with measurement. */
export function Calipers({ className = "", opacity = 0.32, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 600 240" className="w-[28rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {/* main beam */}
          <rect x="20" y="100" width="560" height="40" />
          {/* graduations */}
          {Array.from({ length: 22 }).map((_, i) => (
            <line
              key={i}
              x1={50 + i * 24}
              y1="100"
              x2={50 + i * 24}
              y2={i % 5 === 0 ? "78" : "88"}
            />
          ))}
          {/* fixed jaw */}
          <rect x="20" y="60" width="14" height="80" />
          <rect x="20" y="140" width="14" height="40" />
          {/* sliding jaw */}
          <rect x="270" y="40" width="14" height="100" />
          <rect x="270" y="140" width="14" height="60" />
          {/* display */}
          <rect x="290" y="50" width="120" height="46" />
          <text x="306" y="82" fontSize="22" fill={c} fontFamily="monospace">
            42.05
          </text>
          <text x="386" y="68" fontSize="9" fill={c} fontFamily="monospace">
            mm
          </text>
          {/* measured object */}
          <rect x="40" y="80" width="240" height="40" strokeDasharray="3 4" />
          <line x1="40" y1="200" x2="280" y2="200" />
          <line x1="40" y1="194" x2="40" y2="206" />
          <line x1="280" y1="194" x2="280" y2="206" />
          <text x="140" y="220" fontSize="11" fill={c} fontFamily="monospace">
            42.05 ± 0.01
          </text>
          {/* buttons */}
          <rect x="296" y="100" width="20" height="14" />
          <rect x="324" y="100" width="20" height="14" />
          <rect x="352" y="100" width="20" height="14" />
        </g>
      </svg>
    </Wrap>
  );
}

/** Engineering job — part with section view + dimensions. */
export function EngineeringJob({ className = "", opacity = 0.3, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 500 380" className="w-[30rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {/* flange / part */}
          <circle cx="180" cy="180" r="120" />
          <circle cx="180" cy="180" r="90" />
          <circle cx="180" cy="180" r="32" />
          <circle cx="180" cy="180" r="6" fill={c} />
          {/* bolt holes */}
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const a = (deg * Math.PI) / 180;
            const x = 180 + Math.cos(a) * 105;
            const y = 180 + Math.sin(a) * 105;
            return (
              <g key={deg}>
                <circle cx={x} cy={y} r="8" />
                <circle cx={x} cy={y} r="2" fill={c} />
              </g>
            );
          })}
          {/* hatch (section indicator) */}
          {Array.from({ length: 10 }).map((_, i) => (
            <line
              key={i}
              x1={150 + i * 8}
              y1={300}
              x2={120 + i * 8}
              y2={330}
            />
          ))}
          {/* centre cross */}
          <line x1="50" y1="180" x2="310" y2="180" strokeDasharray="4 4" />
          <line x1="180" y1="50" x2="180" y2="310" strokeDasharray="4 4" />
          {/* dimensions */}
          <line x1="60" y1="340" x2="300" y2="340" />
          <line x1="60" y1="335" x2="60" y2="345" />
          <line x1="300" y1="335" x2="300" y2="345" />
          <text x="155" y="358" fontSize="11" fill={c} fontFamily="monospace">
            Ø 240
          </text>
          {/* leader to bolt circle */}
          <line x1="240" y1="100" x2="340" y2="60" />
          <text x="346" y="58" fontSize="10" fill={c} fontFamily="monospace">
            6 × Ø 16
          </text>
          <text x="346" y="74" fontSize="10" fill={c} fontFamily="monospace">
            PCD 210
          </text>
          {/* title block */}
          <rect x="340" y="180" width="150" height="80" />
          <line x1="340" y1="200" x2="490" y2="200" />
          <line x1="340" y1="222" x2="490" y2="222" />
          <line x1="340" y1="244" x2="490" y2="244" />
          <line x1="410" y1="180" x2="410" y2="260" />
          <text x="346" y="194" fontSize="9" fill={c} fontFamily="monospace">DWG</text>
          <text x="416" y="194" fontSize="9" fill={c} fontFamily="monospace">FLG-118</text>
          <text x="346" y="216" fontSize="9" fill={c} fontFamily="monospace">MAT</text>
          <text x="416" y="216" fontSize="9" fill={c} fontFamily="monospace">EN-1.0570</text>
          <text x="346" y="238" fontSize="9" fill={c} fontFamily="monospace">QTY</text>
          <text x="416" y="238" fontSize="9" fill={c} fontFamily="monospace">5,000</text>
          <text x="346" y="258" fontSize="9" fill={c} fontFamily="monospace">REV</text>
          <text x="416" y="258" fontSize="9" fill={c} fontFamily="monospace">02</text>
        </g>
      </svg>
    </Wrap>
  );
}

/** Forge / press machine. */
export function ForgePress({ className = "", opacity = 0.32, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  return (
    <Wrap className={className} opacity={opacity} animate={!bounce} bounce={bounce}>
      <svg viewBox="0 0 400 460" className="w-[20rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {/* base */}
          <rect x="40" y="400" width="320" height="40" />
          <line x1="80" y1="400" x2="80" y2="380" />
          <line x1="320" y1="400" x2="320" y2="380" />
          {/* frame */}
          <rect x="80" y="80" width="240" height="320" />
          <rect x="100" y="100" width="200" height="280" />
          {/* ram */}
          <rect x="160" y="120" width="80" height="120" />
          <line x1="200" y1="240" x2="200" y2="300" strokeWidth="4" />
          <rect x="170" y="300" width="60" height="20" />
          {/* anvil / die */}
          <rect x="140" y="340" width="120" height="20" />
          <rect x="150" y="360" width="100" height="20" />
          {/* workpiece glow indicator */}
          <circle cx="200" cy="335" r="6" fill={c} opacity="0.4" />
          {/* gauges */}
          <circle cx="320" cy="160" r="22" />
          <line x1="320" y1="160" x2="334" y2="148" />
          <circle cx="320" cy="220" r="22" />
          <line x1="320" y1="220" x2="312" y2="206" />
          {/* labels */}
          <text x="148" y="72" fontSize="10" fill={c} fontFamily="monospace">
            HYDRAULIC PRESS · 600 T
          </text>
        </g>
      </svg>
    </Wrap>
  );
}


/** Horizontal CNC Lathe / Turning Center. */
export function CNCLathe({ className = "", opacity = 0.32, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 640 360" className="w-[34rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {/* main bed */}
          <rect x="60" y="220" width="520" height="50" />
          <line x1="80" y1="270" x2="80" y2="300" />
          <line x1="560" y1="270" x2="560" y2="300" />
          <rect x="60" y="300" width="520" height="20" />
          {/* headstock (left) */}
          <rect x="60" y="120" width="120" height="100" />
          <line x1="60" y1="150" x2="180" y2="150" />
          <line x1="60" y1="190" x2="180" y2="190" />
          <circle cx="120" cy="170" r="20" />
          <circle cx="120" cy="170" r="6" fill={c} />
          {/* spindle / chuck */}
          <rect x="180" y="155" width="40" height="30" />
          <circle cx="240" cy="170" r="22" />
          <line x1="240" y1="148" x2="240" y2="192" />
          <line x1="222" y1="160" x2="258" y2="180" />
          <line x1="258" y1="160" x2="222" y2="180" />
          {/* workpiece bar */}
          <rect x="260" y="164" width="180" height="12" />
          <line x1="260" y1="170" x2="440" y2="170" strokeDasharray="3 4" />
          {/* tailstock (right) */}
          <rect x="460" y="140" width="100" height="80" />
          <polygon points="460,180 445,170 460,160" />
          <line x1="460" y1="160" x2="460" y2="180" />
          {/* tool turret (front) */}
          <circle cx="340" cy="220" r="32" />
          <circle cx="340" cy="220" r="14" />
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const a = (deg * Math.PI) / 180;
            const x = 340 + Math.cos(a) * 28;
            const y = 220 + Math.sin(a) * 28;
            return <rect key={deg} x={x - 4} y={y - 4} width="8" height="8" />;
          })}
          <line x1="340" y1="195" x2="340" y2="170" strokeWidth="3" />
          <polygon points="335,170 345,170 340,162" />
          {/* control panel right */}
          <rect x="560" y="120" width="60" height="120" />
          <rect x="568" y="130" width="44" height="32" />
          <circle cx="576" cy="180" r="5" />
          <circle cx="592" cy="180" r="5" />
          <circle cx="608" cy="180" r="5" />
          <rect x="568" y="196" width="44" height="14" />
          <rect x="568" y="216" width="44" height="14" />
          {/* axis labels */}
          <line x1="430" y1="60" x2="500" y2="60" strokeWidth="1.6" />
          <polygon points="500,60 492,56 492,64" fill={c} />
          <text x="510" y="64" fontSize="11" fill={c} fontFamily="monospace">Z</text>
          <line x1="430" y1="60" x2="430" y2="100" strokeWidth="1.6" />
          <polygon points="430,100 426,92 434,92" fill={c} />
          <text x="436" y="100" fontSize="11" fill={c} fontFamily="monospace">X</text>
          {/* label */}
          <text x="220" y="345" fontSize="11" fill={c} fontFamily="monospace">
            CNC TURNING CENTER · Ø320 × 1000
          </text>
        </g>
      </svg>
    </Wrap>
  );
}

/** Vertical Machining Center (VMC) — detailed view with spindle / column / table. */
export function VMC({ className = "", opacity = 0.32, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 480 540" className="w-[26rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {/* floor base */}
          <rect x="40" y="470" width="400" height="50" />
          <line x1="60" y1="470" x2="60" y2="455" />
          <line x1="420" y1="470" x2="420" y2="455" />
          {/* column (back-right) */}
          <rect x="290" y="60" width="120" height="410" />
          <line x1="290" y1="120" x2="410" y2="120" />
          <line x1="290" y1="180" x2="410" y2="180" />
          <line x1="290" y1="240" x2="410" y2="240" />
          <line x1="290" y1="300" x2="410" y2="300" />
          <line x1="290" y1="360" x2="410" y2="360" />
          {/* Y-axis guideway */}
          <rect x="338" y="60" width="14" height="410" />
          {/* spindle head */}
          <rect x="120" y="160" width="220" height="60" />
          <rect x="135" y="170" width="40" height="40" />
          <text x="146" y="195" fontSize="10" fill={c} fontFamily="monospace">12k</text>
          <text x="146" y="208" fontSize="9" fill={c} fontFamily="monospace">RPM</text>
          {/* Z-axis indicator */}
          <line x1="60" y1="160" x2="60" y2="220" />
          <polygon points="56,220 64,220 60,228" fill={c} />
          <text x="36" y="195" fontSize="11" fill={c} fontFamily="monospace">Z</text>
          {/* spindle shaft */}
          <rect x="190" y="220" width="20" height="60" />
          <line x1="200" y1="280" x2="200" y2="340" strokeWidth="3" />
          {/* tool */}
          <rect x="192" y="340" width="16" height="22" />
          <polygon points="194,362 206,362 200,378" />
          {/* tool magazine (carousel) */}
          <circle cx="380" cy="120" r="36" />
          <circle cx="380" cy="120" r="20" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
            const a = (deg * Math.PI) / 180;
            return (
              <line
                key={deg}
                x1={380 + Math.cos(a) * 20}
                y1={120 + Math.sin(a) * 20}
                x2={380 + Math.cos(a) * 36}
                y2={120 + Math.sin(a) * 36}
              />
            );
          })}
          <text x="356" y="170" fontSize="9" fill={c} fontFamily="monospace">24-TOOL ATC</text>
          {/* X-Y table */}
          <rect x="80" y="380" width="280" height="22" />
          {[0,1,2,3,4,5,6].map((i) => (
            <line key={i} x1={92 + i*40} y1="380" x2={92 + i*40} y2="402" />
          ))}
          {/* work piece */}
          <rect x="180" y="362" width="60" height="18" />
          <line x1="180" y1="371" x2="240" y2="371" strokeDasharray="3 4" />
          {/* base / saddle */}
          <rect x="80" y="402" width="280" height="60" />
          {/* coolant tank */}
          <rect x="80" y="240" width="50" height="100" />
          <line x1="80" y1="260" x2="130" y2="260" />
          <line x1="80" y1="320" x2="130" y2="320" />
          {/* control pendant */}
          <rect x="20" y="280" width="48" height="80" />
          <rect x="26" y="288" width="36" height="22" />
          <circle cx="32" cy="324" r="4" />
          <circle cx="44" cy="324" r="4" />
          <circle cx="56" cy="324" r="4" />
          <rect x="26" y="338" width="36" height="14" />
          {/* main dimension */}
          <line x1="40" y1="500" x2="440" y2="500" />
          <line x1="40" y1="495" x2="40" y2="505" />
          <line x1="440" y1="495" x2="440" y2="505" />
          <text x="200" y="518" fontSize="11" fill={c} fontFamily="monospace">
            VMC · 1050 × 600 × 500
          </text>
        </g>
      </svg>
    </Wrap>
  );
}

/** Forging Process — 3-step sequence (heated billet → hammer strike → finished part). */
export function ForgingProcess({ className = "", opacity = 0.34, tone = "navy", bounce = false }) {
  const c = TONES[tone] || TONES.navy;
  const accent = TONES.orange;
  return (
    <Wrap className={className} opacity={opacity} bounce={bounce}>
      <svg viewBox="0 0 760 280" className="w-[40rem]" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {/* STEP 1 — heated billet on anvil */}
          <text x="40" y="36" fontSize="10" fill={c} fontFamily="monospace" letterSpacing="2">
            01 · HEAT
          </text>
          <rect x="40" y="50" width="180" height="12" />
          {/* glowing billet */}
          <rect x="80" y="100" width="100" height="40" fill={accent} stroke="none" opacity="0.5" />
          <rect x="80" y="100" width="100" height="40" />
          {/* heat waves */}
          <motion.g
            animate={{ y: [0, -6, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          >
            <path d="M 90 90 Q 100 80 110 90 T 130 90 T 150 90 T 170 90" />
            <path d="M 90 78 Q 100 68 110 78 T 130 78 T 150 78 T 170 78" />
          </motion.g>
          {/* anvil */}
          <polygon points="60,140 200,140 220,160 40,160" />
          <rect x="80" y="160" width="100" height="20" />
          <rect x="100" y="180" width="60" height="60" />
          {/* tag */}
          <text x="60" y="262" fontSize="9" fill={c} fontFamily="monospace">
            1200 °C BILLET
          </text>

          {/* arrow → */}
          <line x1="240" y1="140" x2="280" y2="140" />
          <polygon points="280,140 270,135 270,145" fill={c} />

          {/* STEP 2 — hammer strike */}
          <text x="300" y="36" fontSize="10" fill={c} fontFamily="monospace" letterSpacing="2">
            02 · FORGE
          </text>
          <rect x="300" y="50" width="180" height="12" />
          {/* hammer frame */}
          <rect x="320" y="40" width="140" height="12" />
          <line x1="340" y1="52" x2="340" y2="62" />
          <line x1="440" y1="52" x2="440" y2="62" />
          {/* hammer ram (animated up/down) */}
          <motion.g
            animate={{ y: [0, 30, 0] }}
            transition={{ repeat: Infinity, duration: 1.1, ease: "easeInOut" }}
          >
            <rect x="350" y="62" width="80" height="50" />
            <rect x="360" y="112" width="60" height="14" />
          </motion.g>
          {/* die / anvil */}
          <rect x="340" y="160" width="100" height="20" />
          <rect x="320" y="180" width="140" height="20" />
          {/* workpiece being hit (squashed) */}
          <rect x="360" y="144" width="60" height="16" />
          {/* sparks */}
          {Array.from({ length: 6 }).map((_, i) => {
            const angle = (i / 6) * Math.PI - Math.PI / 2;
            const dx = Math.cos(angle) * 28;
            const dy = -Math.abs(Math.sin(angle)) * 24;
            return (
              <motion.circle
                key={i}
                cx={390}
                cy={150}
                r={2}
                fill={accent}
                stroke="none"
                animate={{
                  cx: [390, 390 + dx],
                  cy: [150, 150 + dy + 30],
                  opacity: [1, 0],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 1.1,
                  delay: i * 0.08,
                  ease: "easeOut",
                }}
              />
            );
          })}
          <text x="328" y="262" fontSize="9" fill={c} fontFamily="monospace">
            CLOSED-DIE FORGE
          </text>

          {/* arrow → */}
          <line x1="500" y1="140" x2="540" y2="140" />
          <polygon points="540,140 530,135 530,145" fill={c} />

          {/* STEP 3 — finished forged part */}
          <text x="560" y="36" fontSize="10" fill={c} fontFamily="monospace" letterSpacing="2">
            03 · FINISH
          </text>
          <rect x="560" y="50" width="180" height="12" />
          {/* finished flange */}
          <circle cx="650" cy="150" r="44" />
          <circle cx="650" cy="150" r="32" />
          <circle cx="650" cy="150" r="10" />
          <circle cx="650" cy="150" r="3" fill={c} />
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const a = (deg * Math.PI) / 180;
            const x = 650 + Math.cos(a) * 38;
            const y = 150 + Math.sin(a) * 38;
            return <circle key={deg} cx={x} cy={y} r="3" />;
          })}
          {/* dimension */}
          <line x1="600" y1="210" x2="700" y2="210" />
          <line x1="600" y1="205" x2="600" y2="215" />
          <line x1="700" y1="205" x2="700" y2="215" />
          <text x="612" y="226" fontSize="10" fill={c} fontFamily="monospace">
            Ø 100
          </text>
          <text x="580" y="262" fontSize="9" fill={c} fontFamily="monospace">
            FORGED FLANGE · EN-1.0570
          </text>
        </g>
      </svg>
    </Wrap>
  );
}
